
















import { Component, Vue } from 'vue-property-decorator';
import MainLayout from '../../layouts/MainLayout.vue';
import QuizArea from './QuizArea.vue';
import QuizNav from './QuizNav.vue';
import WordDictionary from '../../utils/WordDictionary';
import HistoryList from './HistoryList.vue';
import IWord from '@/types/IWord';
import ICurrentWord from '@/types/ICurrentWord';
import IQuizStats from '@/types/IQuizStats';
import IQuizError from '@/types/IQuizError';
import IHistoryItem from '@/types/IHistoryItem';
import SubmissionEvent from '@/types/SubmissionEvent';
import SubmissionStatus from '@/types/SubmissionStatus';
import ISubmissionAnswer from '@/types/ISubmissionAnswer';
import QuizCommand from '@/types/QuizCommand';
import messageBus from '../../utils/MessageBus';

// noinspection JSUnusedGlobalSymbols
@Component({
  components: {
    QuizNav,
    QuizArea,
    MainLayout,
    HistoryList,
  },
})
export default class Home extends Vue {
  currentWord: ICurrentWord;
  submission: string;
  stats: IQuizStats;
  error!: IQuizError;
  wordDictionary!: WordDictionary;
  historyList: IHistoryItem[];

  constructor() {
    super();

    this.currentWord = {
      group: '',
      lesson: '',
      word: '',
      audioW: '',
      audioM: '',
    };

    this.submission = '';

    this.stats = {
      completed: 0,
      total: 0,
      seenWords: new Map<string, number>(),
      elapsedSeconds: 0,
    };

    this.clearError();
    this.wordDictionary = new WordDictionary();
    this.historyList = [];
  }

  updateAppWithNewWord(word: IWord): void {
    // merge in updated values
    this.currentWord = {
      ...this.currentWord,
      ...{
        group: word.group,
        lesson: word.lesson,
        word: word.word,
      },
    };

    // update audio tracks
    const audioValue = word.word.replace(/^(der|die|das)[ _]/i, '').toLowerCase();
    this.currentWord.audioW = `audio/w/${audioValue}.mp3`;
    this.currentWord.audioM = `audio/m/${audioValue}.mp3`;

    // set stats
    this.stats.completed = Math.max(0, this.stats.seenWords.size);

    // clear user submission field
    this.submission = '';

    // clear error
    this.clearError();
  }

  playCurrentWord(): void {
    this.currentWordEvent(SubmissionEvent.Heard);
    this.$nextTick(() => messageBus.$emit('playCurrentWord'));
  }

  nextWord(unsee?: boolean): void {
    // consider the current word as "seen" before advancing
    if (!unsee) {
      const currentWord: IWord = this.wordDictionary.words[0];
      if (currentWord) {
        const count: number = this.stats.seenWords.get(currentWord.word) || 0;
        this.stats.seenWords.set(currentWord.word, count + 1);
      }
    }

    // round-robin forward through array of words
    const wordArray: IWord[] = this.wordDictionary.words;
    const prev: IWord | undefined = wordArray.shift();
    if (prev) {
      wordArray.push(prev);
    }
    const word = wordArray[0];

    this.updateAppWithNewWord(word);
  }

  nextAndPlay(unsee?: boolean): void {
    this.nextWord(unsee);
    this.playCurrentWord();
  }

  previousWord(): void {
    // round-robin backwad through array of words
    const wordArray: IWord[] = this.wordDictionary.words;
    const prev: IWord | undefined = wordArray.pop();
    if (prev) {
      wordArray.unshift(prev);
    }
    const word = wordArray[0];

    this.updateAppWithNewWord(word);
  }

  previousAndPlay(): void {
    this.previousWord();
    this.playCurrentWord();
  }

  currentWordEvent(event: SubmissionEvent): void {
    // record statistical event for current word
    this.wordDictionary.addCurrentWordEvent(this.currentWord, event);
  }

  // noinspection JSUnusedLocalSymbols
  beep(status: SubmissionStatus) { // eslint-disable-line class-methods-use-this
    // const success = 'audio/success.mp3';
    // const failure = 'audio/failure.mp3';
    // const beepFunc = () => {
    //   var snd = new Audio(status === SubmissionStatus.Success ? success : failure);
    //   return () => {
    //     return snd.play();
    //   };
    // };
    //
    // return beepFunc()();
  }

  checkAnswer(): void {
    const userAnswer: string = WordDictionary.downcaseArticle(this.submission, true);
    const answer: string = this.currentWord.word;

    // treat empty submit as play
    if (!userAnswer) {
      this.playCurrentWord();
      return;
    }

    let status = SubmissionStatus.Failure;

    // compare user answer
    if (userAnswer === answer) {
      this.clearError();
      this.currentWordEvent(SubmissionEvent.Correct);

      // prepend to history
      this.historyList.unshift({
        id: this.historyList.length + 1,
        text: answer,
      });

      status = SubmissionStatus.Success;
      this.beep(status);
      this.nextAndPlay();
    } else {
      // submission did not match expected answer
      let errorEvent = SubmissionEvent.ErrorIncorrect;
      let errorText = 'ein Fehler';
      let errorStart = userAnswer.length;
      let errorStop = errorStart;

      // if supposed to have an article,
      // see if answer matches ignoring article and case
      //   if so, see if article is present, otherwise, missing article error
      //   if so, see if article matches, otherwise, wrong article error
      //   if so, see if capitalization problem
      if (answer.match(/^(der|die|das) /i)) {
        if (userAnswer.replace(/^(der|die|das) /i, '').toLowerCase()
          === answer.substr(4).toLowerCase()) {
          if (userAnswer.match(/^(der|die|das) /i)) {
            const userArticle: string = userAnswer.substr(0, 4);
            const article: string = answer.substr(0, 4);
            if (userArticle !== article) {
              errorEvent = SubmissionEvent.ErrorWrongArticle;
              errorText = 'falscher Artikel';
              errorStart = 0;
              errorStop = 3;
            } else if (userArticle.toLowerCase() === article.toLowerCase()) {
              errorEvent = SubmissionEvent.ErrorCapitalization;
              errorText = 'fehlende Gro\u00DFschreibung';
              errorStart = 4;
              errorStop = 5;
            }
          } else {
            errorEvent = SubmissionEvent.ErrorMissingArticle;
            errorText = 'fehlender Artikel';
            errorStart = 0;
            errorStop = 1;
            this.submission = ` ${userAnswer}`;
          }
        }
      }

      this.currentWordEvent(errorEvent);
      this.error.errorEvent = errorEvent;
      this.error.message = errorText;
      this.error.range = {
        start: errorStart,
        stop: errorStop,
      };
      this.error.submission = userAnswer;

      this.beep(status);
      this.playCurrentWord();
    }

    // emit event to allow components to highlight success/failure
    const submitted: ISubmissionAnswer = {
      status,
      submission: this.submission,
      submittedAt: Date.now(),
      error: this.error,
    };
    this.$nextTick(() => messageBus.$emit('answerSubmitted', submitted));
  }

  clearError(): void {
    this.error = {
      errorEvent: null,
      message: '',
      range: {
        start: 0,
        stop: 0,
      },
      submission: '',
    };
  }

  onQuizCommand(command: QuizCommand): void {
    if (command === QuizCommand.Play) {
      this.playCurrentWord();
    } else if (command === QuizCommand.Check) {
      this.checkAnswer();
    } else if (command === QuizCommand.Skip) {
      this.nextAndPlay(true);
    } else if (command === QuizCommand.Previous) {
      this.previousAndPlay();
    }
  }

  onKeyup(event: KeyboardEvent): void {
    if (event.key !== 'Enter') { // TODO || !this.disabled) {
      return;
    }

    this.checkAnswer();
    event.preventDefault();
  }

  // noinspection JSUnusedGlobalSymbols
  mounted(): void {
    // words load asynchronously, load first word and enable input when done loading
    this.wordDictionary.init(() => {
      this.stats.total = this.wordDictionary.words.length;
      this.nextWord(true);
      messageBus.$emit('enableInput', true);

      setInterval(() => {
        this.stats.elapsedSeconds += 1; // assumes timer fires once per second
      }, 1000);
    },
    (dict, xhr) => {
      Vue.$log('init failure', dict, xhr);
    });

    // consider Enter key globally
    document.body.addEventListener('keyup', this.onKeyup);

    // handler for quiz commands
    // - play current word
    // - check submission against current word, advance if correct
    // - skip current word
    // - go back to previous word
    messageBus.$on('quizCommand', this.onQuizCommand);
  }

  beforeDestroy(): void {
    messageBus.$off('quizCommand', this.onQuizCommand);
    document.body.removeEventListener('keyup', this.onKeyup);
  }
}
